import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";

/**
 * All data-fetching, debouncing, keyboard-nav and outside-click logic for the
 * company/sector/industry typeahead. Kept separate from CompanySearch.jsx so
 * the component only has to deal with rendering.
 */
export function useCompanySearchTypeahead({ variant, autoFocus }) {
  const [query, setQuery] = useState("");
  const [data, setData] = useState({ reports: [], sectors: [], industries: [] });
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);

  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const timer = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setData({ reports: [], sectors: [], industries: [] });
      setOpen(false);
      return;
    }
    clearTimeout(timer.current);
    setLoading(true);
    timer.current = setTimeout(() => {
      apiClient.get("/public/search", { params: { q, limit: 6 } })
        .then(({ data: res }) => { setData(res); setOpen(true); setActive(0); })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 180);
    return () => clearTimeout(timer.current);
  }, [query]);

  // Global cmd/ctrl+k → focus this search when it's the nav one
  useEffect(() => {
    if (variant !== "nav") return;
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [variant]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const items = useMemo(() => {
    const list = [];
    data.reports.forEach((r) => list.push({ kind: "report", value: r }));
    data.sectors.forEach((s) => list.push({ kind: "sector", value: s }));
    data.industries.forEach((i) => list.push({ kind: "industry", value: i }));
    return list;
  }, [data]);

  const go = (item) => {
    if (item.kind === "report") navigate(`/company/${item.value.slug}`);
    else navigate(`/research/indian-market?filter=${encodeURIComponent(item.value)}`);
    setOpen(false);
    setQuery("");
  };

  const onKey = (e) => {
    if (!open || !items.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % items.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a - 1 + items.length) % items.length); }
    else if (e.key === "Enter") { e.preventDefault(); go(items[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return {
    query, setQuery, data, open, setOpen, active, setActive, loading,
    inputRef, wrapRef, go, onKey,
  };
}
